import { resolveOwnerName, resolveOwnerNameCapitalized } from "../../content/owner-name";

/**
 * Content Design-authored strings for the renewal landing route — screen 2
 * (TG-5, refreshed to H2; design_handoff_renewals_portal_H2/README.md
 * "Screen 2 — Renewal summary"). Every string a customer can see on this
 * route is authored here — no developer-drafted copy, no lorem, no
 * placeholder reaches the component file.
 *
 * Four states, matching AC-10/AC-11:
 *   - notStarted (2.1): no section confirmed yet.
 *   - partiallyComplete (2.2): at least one section confirmed.
 *   - contributor (2.3): a delegated colleague, never an owner.
 *   - failedRead: the renewal summary or working-copy read failed —
 *     worded so a returning customer reads this as a connection problem,
 *     never as their confirmed work having disappeared.
 *
 * `stageLabels` and `stepperStatusText` (Content Design Agent mandatory)
 * are the stage names and status words the landing route's Stepper
 * renders. Keyed by `StepperStageDescriptor.labelKey`
 * (`routing/route-table.ts`'s `STEPPER_STAGE_TABLE`) and by
 * `StepperStageState` respectively, mirroring
 * `features/renewal-capture/content.ts`'s established
 * `sectionLabels`/`getSectionLabel` pattern — no developer-drafted wording
 * reaches `RenewalLanding.tsx` for these either.
 */
export const renewalLandingContent = {
  stageLabels: {
    "routing.stage.confirmDetails.label": "Confirm details",
    "routing.stage.reviewExposures.label": "Review exposures",
    "routing.stage.declaration.label": "Declaration",
  } as Record<string, string>,

  stepperStatusText: {
    done: "Completed",
    current: "In progress",
    todo: "To do",
  } as const,

  loadingMessage: "Checking your renewal...",
  stepperLabel: "Renewal steps",

  notStarted: {
    statusLabel: "Not started",
    bodyText: "Check your details are up to date. If nothing has changed, this will only take a couple of minutes.",
    primaryActionLabel: "Start renewal",
  },

  partiallyComplete: {
    statusLabel: "In progress",
    /** AC-10's own quoted text ("You have completed <n> of 3 steps."),
     * `<n>` substituted at render time, PLUS the second sentence H2's own
     * screen 2.2 screenshot and `02-Landing.dc.html` carry ("Reviewing
     * your exposures is next.") -- dropped from the original build
     * (ADR-20261016-v6nd, AC-2's "missing line"). H2 pictures exactly one
     * partially-complete state (business-details alone confirmed, next
     * stop exposures), so only that one next-step description is drawn
     * from H2 directly; `nextStepDescription` below supplies no
     * H2-sourced text for "declaration" (2 of 3 confirmed, next stop the
     * declaration) -- `RenewalLanding.tsx` omits the second sentence
     * entirely rather than render invented copy for a state H2 never
     * shows. `RenewalLanding.tsx` is the one caller. */
    bodyText: (confirmedCount: number, nextStepDescription?: string) =>
      nextStepDescription === undefined
        ? `You have completed ${confirmedCount} of 3 steps.`
        : `You have completed ${confirmedCount} of 3 steps. ${nextStepDescription} is next.`,
    primaryActionLabel: "Continue renewal",
  },

  /** Keyed by `StepId` (`routing/route-table.ts`). Only "exposures" is
   * H2-sourced (see `partiallyComplete.bodyText`'s own doc comment) --
   * deliberately no "declaration" entry. */
  nextStepDescription: {
    exposures: "Reviewing your exposures",
  } as Record<string, string>,

  /** AC-11, H2's 2.3: H2's own worked example names "Dana Whitfield" and
   * "2 sections" — `ownerName`/`sectionCount` are substituted at render
   * time, never hardcoded here. ADR-20261016-mew4: `ownerName` is
   * nullable (an instance exposure-prepared before this feature shipped)
   * — `resolveOwnerName`/`resolveOwnerNameCapitalized` substitute the
   * Content Design Agent's own fallback copy in that case. */
  contributor: {
    statusLine: (ownerName: string | null, sectionCount: number) =>
      `${resolveOwnerNameCapitalized(ownerName)} has asked you to assist with ${sectionCount} section${sectionCount === 1 ? "" : "s"} of this renewal. ` +
      `You are not required to sign or submit anything. Once all sections have been completed, ${resolveOwnerName(ownerName)} will review, sign, and submit the renewal.`,
    primaryActionLabel: "Start",
  },

  failedRead: {
    title: "We could not load your renewal",
    message: "Something has gone wrong on our end. Nothing you've already confirmed has been lost. Try again.",
    retryActionLabel: "Try again",
  },
} as const;

export type RenewalLandingContent = typeof renewalLandingContent;

/** Looks up a stepper stage's display label by its declared `labelKey`
 * (`STEPPER_STAGE_TABLE`'s own field), mirroring
 * `features/renewal-capture/content.ts`'s `getSectionLabel`. Falls back to
 * the key itself only if a future stage's copy has not yet been authored —
 * never fabricates a business value. */
export function getStageLabel(labelKey: string): string {
  return renewalLandingContent.stageLabels[labelKey] ?? labelKey;
}

/** Looks up the Content-Design-authored status word for a resolved
 * `StepperStageState` ("done" | "current" | "todo"). Closed input union —
 * every value renders verbatim text, never a fallback. */
export function getStepperStatusText(state: "done" | "current" | "todo"): string {
  return renewalLandingContent.stepperStatusText[state];
}
